import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { lerVersao } from '@/utils/versao';
import { CHAVE_DA_TENTATIVA, deveMostrarPilula, deveTrocarNaNavegacao, INTERVALO_DA_CONFERENCIA_MS, lerVersaoPublicada } from './versaoNova';

const lerTentada = (): string | null => { try { return sessionStorage.getItem(CHAVE_DA_TENTATIVA); } catch { return null; } };
const anotarTentada = (v: string) => { try { sessionStorage.setItem(CHAVE_DA_TENTATIVA, v); } catch { /* sem armazenamento: só não evita a 2ª tentativa */ } };

/** Recarrega pedindo antes ao service worker que confira o dele (o `sw.ts` já busca o `index.html` na rede). */
async function recarregar(publicada: string) {
  anotarTentada(publicada);
  // Com teto: a conferência do service worker não pode segurar a recarga (o `sw.ts` já pede o `index.html` à rede).
  const conferirOServiceWorker = (async () => { try { await (await navigator.serviceWorker?.getRegistration())?.update(); } catch { /* recarrega assim mesmo */ } })();
  await Promise.race([conferirOServiceWorker, new Promise((ok) => setTimeout(ok, 1500))]);
  window.location.reload();
}

/*
 * Fora do componente, e não em estado: o limite de erro global (`ErroNaTela`) usa `key={pathname}`, então tudo o que está
 * dentro dele é REMONTADO a cada troca de tela — em estado, a versão vista e o caminho anterior se perdiam na navegação
 * em que a troca devia acontecer (medido na cópia, 24/10/2026).
 */
let publicadaVista: string | null = null;
let caminhoAnterior: string | null = null;

/** "Nova versão do portal · Atualizar" — só no navegador; regra em `versaoNova.ts`. Montado uma vez, em `App.tsx`. */
export function AvisoDeVersaoNova() {
  const { pathname } = useLocation();
  const minha = lerVersao().web;
  const [publicada, setPublicada] = useState<string | null>(publicadaVista);
  const nativo = Capacitor.isNativePlatform();

  const conferir = useCallback(async () => {
    if (nativo || document.visibilityState !== 'visible') return;
    const v = await lerVersaoPublicada();
    if (v) { publicadaVista = v; setPublicada(v); }
  }, [nativo]);

  useEffect(() => {
    if (nativo) return;
    const primeira = setTimeout(() => void conferir(), 3000);
    const intervalo = setInterval(() => void conferir(), INTERVALO_DA_CONFERENCIA_MS);
    const aoVoltar = () => { if (document.visibilityState === 'visible') void conferir(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    return () => { clearTimeout(primeira); clearInterval(intervalo); document.removeEventListener('visibilitychange', aoVoltar); window.removeEventListener('focus', aoVoltar); };
  }, [nativo, conferir]);

  // A troca sozinha, na próxima navegação: o endereço novo já está na barra, e a recarga o abre na versão nova.
  useEffect(() => {
    const de = caminhoAnterior;
    caminhoAnterior = pathname;
    if (de == null || de === pathname || !publicada) return;
    if (deveTrocarNaNavegacao({ publicada, minha, de, para: pathname, tentada: lerTentada() })) void recarregar(publicada);
  }, [pathname, publicada, minha]);

  if (nativo || !deveMostrarPilula({ publicada, minha, pathname })) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none">
      <button type="button" onClick={() => publicada && void recarregar(publicada)} data-testid="versao-nova"
        className="pointer-events-auto flex min-h-[44px] items-center gap-2 rounded-full bg-gray-900/90 px-4 py-2 text-sm text-white shadow-lg">
        <span>Nova versão do portal</span><span aria-hidden>·</span><span className="font-semibold underline">Atualizar</span>
      </button>
    </div>
  );
}
